import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Microscope, ArrowLeft, Trophy, Star, 
  RotateCcw, ZoomIn, ZoomOut, Eye, CircleDot
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const VirtualMicroscopeGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  const slides = [
    {
      id: 1,
      name: 'Plant Cell',
      image: '🌿',
      zoomImages: ['🟢', '🔬', '🧬'],
      description: 'A cell from a leaf showing the cell wall and chloroplasts',
      question: 'What is the green structure that helps plants make food?',
      options: ['Cell Wall', 'Chloroplast', 'Nucleus', 'Mitochondria'],
      correctAnswer: 1,
      explanation: 'Chloroplasts contain chlorophyll which captures sunlight for photosynthesis!',
      facts: ['Plants cells have a rigid cell wall', 'Chloroplasts give plants their green color', 'The vacuole stores water and nutrients'],
      points: 20
    },
    {
      id: 2,
      name: 'Animal Cell',
      image: '🔴',
      zoomImages: ['⭕', '🔬', '🧬'],
      description: 'A human cheek cell showing typical animal cell structures',
      question: 'Which organelle is the "powerhouse" of the cell?',
      options: ['Nucleus', 'Ribosome', 'Mitochondria', 'Golgi Body'],
      correctAnswer: 2,
      explanation: 'Mitochondria produce ATP - the energy currency of cells!',
      facts: ['Animal cells have no cell wall', 'The nucleus contains DNA', 'Mitochondria have their own DNA'],
      points: 20
    },
    {
      id: 3,
      name: 'Bacteria',
      image: '🦠',
      zoomImages: ['💊', '🔬', '🧬'],
      description: 'Rod-shaped bacteria (bacillus) commonly found everywhere',
      question: 'What type of cell are bacteria?',
      options: ['Eukaryotic', 'Prokaryotic', 'Plant cell', 'Virus'],
      correctAnswer: 1,
      explanation: 'Bacteria are prokaryotes - they have no nucleus or membrane-bound organelles!',
      facts: ['Bacteria are single-celled organisms', 'Some bacteria are helpful (probiotics)', 'Bacteria reproduce by binary fission'],
      points: 20
    },
    {
      id: 4,
      name: 'Blood Cells',
      image: '🩸',
      zoomImages: ['⚪', '🔬', '🧬'],
      description: 'Human blood showing red and white blood cells',
      question: 'What do red blood cells carry around the body?',
      options: ['Carbon Dioxide', 'Oxygen', 'Nutrients', 'Hormones'],
      correctAnswer: 1,
      explanation: 'Red blood cells contain hemoglobin which binds to oxygen and carries it to all body tissues!',
      facts: ['Red blood cells have no nucleus', 'White blood cells fight infection', 'Blood also contains platelets for clotting'],
      points: 20
    },
    {
      id: 5,
      name: 'Onion Skin Cells',
      image: '🧅',
      zoomImages: ['🟨', '🔬', '🧬'],
      description: 'Thin layer of onion epidermis showing rectangular cells',
      question: 'What gives plant cells their rectangular shape?',
      options: ['Nucleus', 'Vacuole', 'Cell Wall', 'Cytoplasm'],
      correctAnswer: 2,
      explanation: 'The rigid cell wall made of cellulose gives plant cells their fixed rectangular shape!',
      facts: ['Onion cells are easy to see under a microscope', 'Iodine stain makes the nucleus visible', 'Each cell has a large central vacuole'],
      points: 20
    },
    {
      id: 6,
      name: 'Nerve Cell (Neuron)',
      image: '⚡',
      zoomImages: ['🔌', '🔬', '🧬'],
      description: 'A motor neuron showing the cell body and axon',
      question: 'What is the long extension of a nerve cell called?',
      options: ['Dendrite', 'Axon', 'Synapse', 'Myelin'],
      correctAnswer: 1,
      explanation: 'The axon carries electrical signals away from the cell body to other neurons or muscles!',
      facts: ['Some axons can be over 1 meter long', 'Neurons communicate via electrical impulses', 'The brain has about 86 billion neurons'],
      points: 20
    },
    {
      id: 7,
      name: 'Amoeba',
      image: '🫧',
      zoomImages: ['💧', '🔬', '🧬'],
      description: 'Single-celled organism that changes shape to move',
      question: 'How does an amoeba move?',
      options: ['Using flagella', 'Using cilia', 'Using pseudopods', 'It cannot move'],
      correctAnswer: 2,
      explanation: 'Amoeba extend pseudopods (false feet) to crawl and engulf food!',
      facts: ['Amoeba are found in ponds and soil', 'They eat bacteria and algae', 'Amoeba reproduce by splitting in two'],
      points: 20
    },
    {
      id: 8,
      name: 'Muscle Tissue',
      image: '💪',
      zoomImages: ['🏋️', '🔬', '🧬'],
      description: 'Striated muscle fibers showing striped pattern',
      question: 'What type of muscle is attached to bones?',
      options: ['Smooth muscle', 'Cardiac muscle', 'Skeletal muscle', 'Involuntary muscle'],
      correctAnswer: 2,
      explanation: 'Skeletal muscles are voluntary muscles that attach to bones and allow us to move!',
      facts: ['Skeletal muscles have a striped appearance', 'Cardiac muscle is only in the heart', 'Smooth muscle is found in organs'],
      points: 20
    }
  ];

  const currentSlideData = slides[currentSlide];

  const startGame = () => {
    setGameState('playing');
    setCurrentSlide(0);
    setZoomLevel(1);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setCorrectAnswers(0);
  };

  const handleZoom = (direction) => {
    if (direction === 'in' && zoomLevel < 3) {
      setZoomLevel(z => z + 1);
    } else if (direction === 'out' && zoomLevel > 1) {
      setZoomLevel(z => z - 1);
    }
  };

  const handleAnswer = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const checkAnswer = () => {
    if (selectedAnswer === null) {
      toast.error('Select an answer first!');
      return;
    }

    setShowResult(true);
    if (selectedAnswer === currentSlideData.correctAnswer) {
      setScore(s => s + currentSlideData.points);
      setCorrectAnswers(c => c + 1);
      toast.success('Correct! Great observation!');
    } else {
      toast.error('Not quite right. Check the explanation!');
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(c => c + 1);
      setZoomLevel(1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      endGame();
    }
  };

  const endGame = async () => {
    setGameState('finished');
    
    try {
      await api.post('/games/complete', {
        game_id: 'virtual-microscope',
        score: score,
        metadata: { 
          slides_completed: correctAnswers,
          total_slides: slides.length
        }
      });
      toast.success(`🔬 You earned ${score} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  return (
    <StudentLayout>
      <div data-testid="virtual-microscope-game" className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
        </div>

        {/* Game Header */}
        <div className="student-card bg-indigo-100 border-indigo-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center">
                <Microscope className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Virtual Microscope</h1>
                <p className="text-slate-600">
                  {gameState === 'playing' 
                    ? `Slide ${currentSlide + 1} of ${slides.length}` 
                    : 'Explore the microscopic world!'}
                </p>
              </div>
            </div>
            {gameState === 'playing' && (
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-500" />
                <span className="font-heading text-xl font-bold text-slate-800">{score}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-indigo-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <Microscope className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Virtual Microscope</h2>
            <p className="text-lg text-slate-600 mb-8">
              Explore cells and organisms under the microscope!<br/>
              Zoom in to discover the hidden world of biology.
            </p>
            <div className="flex justify-center gap-6 mb-8">
              <div className="text-center">
                <ZoomIn className="w-10 h-10 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Zoom & explore</p>
              </div>
              <div className="text-center">
                <Eye className="w-10 h-10 text-purple-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Observe details</p>
              </div>
              <div className="text-center">
                <CircleDot className="w-10 h-10 text-pink-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Identify structures</p>
              </div>
            </div>
            <Button onClick={startGame} className="bg-indigo-500 hover:bg-indigo-600 text-white text-lg px-10 py-6">
              Start Exploring
              <Microscope className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentSlideData && (
          <div className="student-card">
            {/* Slide Name */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-800">
                {currentSlideData.name}
              </h2>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleZoom('out')}
                  disabled={zoomLevel <= 1}
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm font-medium px-3">{zoomLevel}00x</span>
                <button 
                  onClick={() => handleZoom('in')}
                  disabled={zoomLevel >= 3}
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Microscope View */}
            <div className="relative bg-slate-900 rounded-full w-64 h-64 mx-auto mb-6 overflow-hidden border-8 border-slate-700">
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`transition-all duration-300 ${
                  zoomLevel === 1 ? 'text-6xl' : zoomLevel === 2 ? 'text-8xl' : 'text-9xl'
                }`}>
                  {currentSlideData.zoomImages[zoomLevel - 1]}
                </span>
              </div>
              {/* Crosshairs */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-full h-px bg-indigo-400/30"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="h-full w-px bg-indigo-400/30"></div>
              </div>
            </div>

            {/* Description */}
            <p className="text-center text-slate-600 mb-6">{currentSlideData.description}</p>

            {/* Fun Facts */}
            <div className="bg-indigo-50 rounded-xl p-4 mb-6">
              <p className="font-semibold text-indigo-800 mb-2">🔬 Did you know?</p>
              <ul className="text-sm text-indigo-700 space-y-1">
                {currentSlideData.facts.map((fact, i) => (
                  <li key={i}>• {fact}</li>
                ))}
              </ul>
            </div>

            {/* Question */}
            <div className="bg-slate-100 rounded-xl p-4 mb-4">
              <p className="font-semibold text-slate-800">{currentSlideData.question}</p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {currentSlideData.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  disabled={showResult}
                  className={`p-4 rounded-xl border-2 font-semibold transition-all ${
                    showResult
                      ? idx === currentSlideData.correctAnswer
                        ? 'bg-emerald-100 border-emerald-500 text-emerald-800'
                        : idx === selectedAnswer
                          ? 'bg-red-100 border-red-500 text-red-800'
                          : 'bg-white border-slate-200 text-slate-400'
                      : selectedAnswer === idx
                        ? 'bg-indigo-500 border-indigo-600 text-white'
                        : 'bg-white hover:bg-indigo-50 border-slate-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            {/* Check/Next Button */}
            {!showResult ? (
              <Button 
                onClick={checkAnswer}
                disabled={selectedAnswer === null}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Check Answer
              </Button>
            ) : (
              <div>
                <div className={`rounded-xl p-4 mb-4 ${
                  selectedAnswer === currentSlideData.correctAnswer ? 'bg-emerald-100' : 'bg-amber-100'
                }`}>
                  <p className={`font-semibold ${
                    selectedAnswer === currentSlideData.correctAnswer ? 'text-emerald-800' : 'text-amber-800'
                  }`}>
                    {currentSlideData.explanation}
                  </p>
                </div>
                <Button onClick={nextSlide} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                  {currentSlide < slides.length - 1 ? 'Next Slide' : 'See Results'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">Lab Complete!</h2>
            <p className="text-xl text-slate-600 mb-4">
              You identified {correctAnswers} out of {slides.length} specimens correctly!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">+{score}</span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default VirtualMicroscopeGame;
